import React, { useEffect, useRef } from 'react';
import type { LiveEffectEvent } from '../../api/payment/live-effect.api';
import { clampEffectDurationSec } from '../../utils/live-effect';
import { playBoom, playPop, playScream } from './effect-sounds';
import MemberBadge from '../../components/membership/member-badge';

export const OVERLAY_CODES = [
  'fireworks',
  'emoji_rain',
  'sticker_rain',
  'shout_name',
  'strobe',
  'fog',
  'bomb',
  'jumpscare',
];

const EFFECT_LABEL: Record<string, string> = {
  fireworks: 'bắn pháo hoa',
  emoji_rain: 'thả mưa emoji',
  sticker_rain: 'thả mưa sticker',
  shout_name: 'gọi tên thật to',
  strobe: 'bật nháy đèn',
  fog: 'thả sương mù',
  bomb: 'ném bom',
  jumpscare: 'hù doạ',
};

const CSS = `
@keyframes le-fall { from { transform: translateY(-15vh) rotate(0deg); } to { transform: translateY(115vh) rotate(360deg); } }
@keyframes le-shake { 0%,100% { transform: translate(-50%,-50%) rotate(0); } 25% { transform: translate(-52%,-48%) rotate(-4deg); } 75% { transform: translate(-48%,-52%) rotate(4deg); } }
@keyframes le-pop { 0% { transform: translate(-50%,-50%) scale(0.2); opacity: 0; } 15% { transform: translate(-50%,-50%) scale(1.15); opacity: 1; } 100% { transform: translate(-50%,-50%) scale(1); opacity: 1; } }
@keyframes le-flash { from { opacity: 1; } to { opacity: 0; } }
`;

// captionRight: khung thầu đang nằm bên trái thì dời dòng chữ sang phải để không đè lên nhau.
const Caption: React.FC<{ event: LiveEffectEvent; captionRight?: boolean }> = ({
  event,
  captionRight,
}) => (
  <div
    style={{
      position: 'absolute',
      ...(captionRight ? { right: 32 } : { left: 32 }),
      bottom: 32,
      padding: '10px 18px',
      borderRadius: 12,
      background: 'rgba(0,0,0,0.65)',
      color: '#fff',
      font: '700 34px/1.2 system-ui, sans-serif',
    }}
  >
    {event.viewerName}
    {event.isMember && (
      <MemberBadge size={30} className="inline-block ml-1 align-text-bottom" />
    )}{' '}
    {EFFECT_LABEL[event.code ?? ''] ?? 'bấm hiệu ứng'}
  </div>
);

/** Pháo hoa bằng canvas: mỗi 600ms nổ một chùm hạt ở vị trí ngẫu nhiên. */
const Fireworks: React.FC = () => {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = ref.current;
    const c = canvas?.getContext('2d');
    if (!canvas || !c) return undefined;
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    type P = {
      x: number;
      y: number;
      vx: number;
      vy: number;
      life: number;
      color: string;
    };
    const parts: P[] = [];
    const burst = () => {
      playPop();
      const x = canvas.width * (0.15 + Math.random() * 0.7);
      const y = canvas.height * (0.15 + Math.random() * 0.45);
      const color = `hsl(${Math.floor(Math.random() * 360)},100%,60%)`;
      for (let i = 0; i < 90; i++) {
        const a = (Math.PI * 2 * i) / 90;
        const s = 2 + Math.random() * 5;
        parts.push({
          x,
          y,
          vx: Math.cos(a) * s,
          vy: Math.sin(a) * s,
          life: 70 + Math.random() * 30,
          color,
        });
      }
    };
    burst();
    const burstId = window.setInterval(burst, 600);
    let raf = 0;
    const frame = () => {
      c.clearRect(0, 0, canvas.width, canvas.height);
      for (let i = parts.length - 1; i >= 0; i--) {
        const p = parts[i];
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.06;
        p.life -= 1;
        if (p.life <= 0) {
          parts.splice(i, 1);
          continue;
        }
        c.globalAlpha = Math.min(1, p.life / 40);
        c.fillStyle = p.color;
        c.beginPath();
        c.arc(p.x, p.y, 3, 0, Math.PI * 2);
        c.fill();
      }
      raf = window.requestAnimationFrame(frame);
    };
    raf = window.requestAnimationFrame(frame);
    return () => {
      window.clearInterval(burstId);
      window.cancelAnimationFrame(raf);
    };
  }, []);
  return <canvas ref={ref} style={{ position: 'absolute', inset: 0 }} />;
};

const STICKERS = [
  '<svg viewBox="0 0 100 100"><polygon points="50,5 61,38 96,38 68,59 79,93 50,72 21,93 32,59 4,38 39,38" fill="#FFD43B"/></svg>',
  '<svg viewBox="0 0 100 100"><path d="M50 88 L12 50 A22 22 0 0 1 50 18 A22 22 0 0 1 88 50 Z" fill="#FF4D6D"/></svg>',
  '<svg viewBox="0 0 120 60"><rect width="120" height="60" rx="14" fill="#0057FF"/><text x="60" y="41" font-size="30" font-family="system-ui" font-weight="800" fill="#fff" text-anchor="middle">CAB</text></svg>',
];

/**
 * Hạt mưa cho một lượt: mỗi hạt rơi 2–4 giây (không quá thời lượng) và bắt đầu sao cho rơi xong trước khi lượt kết thúc.
 * Thời lượng kẹp 3–15 giây giống hẹn giờ kết thúc lượt.
 */
export const rainDrops = (
  durationSec: number,
  count = 60,
  rand: () => number = Math.random
) => {
  const total = clampEffectDurationSec(durationSec);
  return Array.from({ length: count }, (_, i) => {
    const dur = Math.min(total, 2 + rand() * 2);
    return {
      left: rand() * 100,
      delay: rand() * (total - dur),
      dur,
      size: 36 + rand() * 40,
      sticker: STICKERS[i % STICKERS.length],
    };
  });
};

/** Sương mù phủ 70% thời lượng rồi tan dần trong 30% còn lại, tan hết đúng lúc lượt kết thúc. */
export const fogTiming = (durationSec: number) => {
  const total = clampEffectDurationSec(durationSec);
  return { delaySec: total * 0.7, fadeSec: total * 0.3 };
};

const Rain: React.FC<{
  emoji?: string | null;
  stickers?: boolean;
  durationSec: number;
}> = ({ emoji, stickers, durationSec }) => {
  const drops = useRef(rainDrops(durationSec)).current;
  return (
    <>
      {drops.map((d, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            top: 0,
            left: `${d.left}%`,
            width: d.size,
            fontSize: d.size,
            animation: `le-fall ${d.dur}s linear ${d.delay}s both`,
          }}
          {...(stickers
            ? { dangerouslySetInnerHTML: { __html: d.sticker } }
            : {})}
        >
          {stickers ? undefined : emoji}
        </div>
      ))}
    </>
  );
};

const Strobe: React.FC = () => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const colors = ['#ff004c', '#00e5ff', '#ffe600', '#7c4dff', '#00ff85'];
    let i = 0;
    // Đổi màu mỗi 400 ms (2,5 lần/giây), dưới trần 3 lần/giây an toàn cho người nhạy cảm ánh sáng chớp.
    const id = window.setInterval(() => {
      if (ref.current) {
        ref.current.style.background = colors[i % colors.length];
      }
      i += 1;
    }, 400);
    return () => window.clearInterval(id);
  }, []);
  return (
    <div ref={ref} style={{ position: 'absolute', inset: 0, opacity: 0.55 }} />
  );
};

const CRACK =
  '<svg viewBox="0 0 1920 1080" preserveAspectRatio="none"><g stroke="#fff" stroke-width="5" fill="none" opacity="0.9"><path d="M960 540 L700 120 M960 540 L1300 80 M960 540 L1800 420 M960 540 L1500 1000 M960 540 L820 1060 M960 540 L150 900 M960 540 L80 300"/><path d="M820 320 L980 300 L1120 360 M760 700 L940 760 L1160 690 M600 480 L700 600" stroke-width="3"/></g></svg>';

const GHOST =
  '<svg viewBox="0 0 200 240"><path d="M100 10 C45 10 20 55 20 110 L20 230 L45 205 L70 230 L100 205 L130 230 L155 205 L180 230 L180 110 C180 55 155 10 100 10 Z" fill="#f5f5f5"/><ellipse cx="70" cy="100" rx="18" ry="26" fill="#111"/><ellipse cx="130" cy="100" rx="18" ry="26" fill="#111"/><ellipse cx="100" cy="165" rx="24" ry="34" fill="#111"/></svg>';

/** Chạy 1 lượt hiệu ứng nhóm thường trong durationSec rồi gọi onDone. Mã không thuộc nhóm thường thì gọi onDone ngay. */
export const OverlayEffect: React.FC<{
  event: LiveEffectEvent;
  onDone: () => void;
  captionRight?: boolean;
}> = ({ event, onDone, captionRight }) => {
  useEffect(() => {
    if (!OVERLAY_CODES.includes(event.code ?? '')) {
      onDone();
      return undefined;
    }
    if (event.code === 'bomb') playBoom();
    if (event.code === 'jumpscare') playScream();
    const id = window.setTimeout(
      onDone,
      clampEffectDurationSec(event.durationSec) * 1000
    );
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event.triggerId]);

  const { code } = event;
  const fog = fogTiming(event.durationSec);
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
      }}
    >
      <style>{CSS}</style>
      {code === 'fireworks' && <Fireworks />}
      {code === 'emoji_rain' && (
        <Rain emoji={event.emoji} durationSec={event.durationSec} />
      )}
      {code === 'sticker_rain' && (
        <Rain stickers durationSec={event.durationSec} />
      )}
      {code === 'shout_name' && (
        <div
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            color: '#fff',
            font: '900 140px/1 system-ui, sans-serif',
            textShadow: '0 0 24px #000, 0 0 6px #0057FF',
            whiteSpace: 'nowrap',
            animation: 'le-shake 0.18s linear infinite',
          }}
        >
          {event.viewerName}
          {event.isMember && (
            <MemberBadge
              size={120}
              className="inline-block ml-4 align-baseline"
            />
          )}
        </div>
      )}
      {code === 'strobe' && <Strobe />}
      {code === 'fog' && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(circle at 50% 50%, rgba(255,255,255,0.95), rgba(235,235,235,0.85))',
            animation: `le-flash ${fog.fadeSec}s ease-in ${fog.delaySec}s both`,
          }}
        />
      )}
      {code === 'bomb' && (
        <>
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: '#fff',
              animation: 'le-flash 0.6s ease-out both',
            }}
          />
          <div
            style={{ position: 'absolute', inset: 0 }}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: CRACK }}
          />
        </>
      )}
      {code === 'jumpscare' && (
        <div
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            width: '60vh',
            animation: 'le-pop 0.5s ease-out both',
          }}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: GHOST }}
        />
      )}
      <Caption event={event} captionRight={captionRight} />
    </div>
  );
};
